import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/context/language-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '@/context/auth'; // Needed to get user ID for upload path
import { supabase } from '@/integrations/supabase/client'; // Needed for upload
import { toast } from '@/components/ui/use-toast';

// Helper function to draw the cropped image onto a canvas
function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: PixelCrop,
) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );

  ctx.restore();
}

const ProfileEditForm: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [aspect, setAspect] = useState<number | undefined>(1); // Aspect ratio 1:1 pour un avatar carré/rond
  const [isUploading, setIsUploading] = useState(false);

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Réinitialiser le crop précédent
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || ''),
      );
      reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height));
    }
  }

  // Dessiner la prévisualisation quand le recadrage est terminé
  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        completedCrop,
      );
    }
  }, [completedCrop]);

  const handleUploadCroppedImage = async () => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current || !user) {
      toast({ title: t('profile.error_cropping'), description: t('profile.crop_image_first'), variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    const canvas = previewCanvasRef.current;
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast({ title: t('profile.error_uploading'), description: t('profile.cannot_create_blob'), variant: 'destructive' });
        setIsUploading(false);
        return;
      }

      const fileName = `${user.id}_${Date.now()}.png`;
      const filePath = `avatars/${fileName}`;

      try {
        const { data, error: uploadError } = await supabase.storage
          .from('avatars') // Assurez-vous que ce bucket existe et a les bonnes politiques RLS
          .upload(filePath, blob, { 
            contentType: 'image/png', 
            upsert: true 
          });

        if (uploadError) throw uploadError;

        // Obtenir l'URL publique
        const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
        const publicUrl = urlData.publicUrl;

        // Mettre à jour le profil utilisateur avec la nouvelle URL
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: publicUrl })
          .eq('id', user.id);

        if (updateError) throw updateError;

        toast({ title: t('profile.success'), description: t('profile.avatar_updated') });
        setImgSrc(''); // Réinitialiser après succès
        setCrop(undefined);
        setCompletedCrop(undefined);
        // Optionnel: rafraîchir les données utilisateur/profil dans l'app

      } catch (error: any) {
        console.error('Error uploading image:', error);
        toast({ title: t('profile.error_uploading'), description: error.message || t('profile.generic_upload_error'), variant: 'destructive' });
      } finally {
        setIsUploading(false);
      }
    }, 'image/png');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profile.edit_profile') || 'Modifier votre profil'}</CardTitle>
        <CardDescription>{t('profile.update_your_details') || 'Mettez à jour vos informations personnelles.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section pour l'upload de l'avatar */}
        <div>
          <Label htmlFor="picture">{t('profile.profile_picture') || 'Photo de profil'}</Label>
          <Input id="picture" type="file" accept="image/*" onChange={onSelectFile} className="mt-1" disabled={isUploading} />
          <p className="text-sm text-muted-foreground mt-1">{t('profile.picture_recommendation') || 'Choisissez une image claire de votre visage.'}</p>
        </div>

        {imgSrc && (
          <div className="space-y-4">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              circularCrop={true} // Pour un aperçu rond dans l'outil
            >
              <img
                ref={imgRef}
                alt="Crop me" // TODO: Traduire
                src={imgSrc}
                onLoad={onImageLoad}
                style={{ maxHeight: '400px' }} // Limiter la hauteur de l'image source
              />
            </ReactCrop>
            
            {/* Zone de prévisualisation */}
            {!!completedCrop && (
              <div>
                <h4 className="text-sm font-medium mb-2">{t('profile.preview') || 'Aperçu'}</h4>
                <canvas
                  ref={previewCanvasRef}
                  style={{
                    border: '1px solid black',
                    objectFit: 'contain',
                    width: 100, // Taille de l'aperçu
                    height: 100,
                    borderRadius: '50%' // Rendre l'aperçu rond
                  }}
                />
              </div>
            )}

            <Button onClick={handleUploadCroppedImage} disabled={!completedCrop || isUploading}>
              {isUploading ? (t('profile.uploading') || 'Téléchargement...') : (t('profile.upload_picture') || 'Télécharger la photo')}
            </Button>
          </div>
        )}

        {/* TODO: Ajouter ici les autres champs du formulaire (displayName, bio, etc.) */}
        
      </CardContent>
    </Card>
  );
};

export default ProfileEditForm; 